import { Component, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectorRef, EventEmitter, Output, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { EMPTY, from, merge, Observable, Subject, Subscription } from 'rxjs';
import { ChatService } from '../../chat.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@aihub/core/authorization';
import { DomSanitizer } from '@angular/platform-browser';
import { PdfReaderService } from '../../pdf-reader.service';
import * as XLSX from 'xlsx';
import { catchError, debounceTime, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import * as SAMPLE_DATA from './sample.constant';
import * as PROMPTS from './prompt.constant';
import { Chat, Message, MessageData } from '../../types';
import { MatDialog } from '@angular/material/dialog';
import { BizbrainWarningDialog } from './bizbrain-warning-dialog';

@Component({
    selector: 'app-bizbrain',
    templateUrl: './bizbrain.component.html',
    styleUrls: ['./bizbrain.component.scss'],
})
export class BizbrainComponent implements OnInit {
    @Input() subscription_id: string = '';
    @Input() response;
    @Output() displayResponse = new EventEmitter<any>();
    @Output() clearResponse = new EventEmitter<any>();
    text: string = '';
    audio;
    audioBlobUrl;
    stopPalm = false;
    public formJson;
    form: FormGroup;
    public uploadIcon = false;
    showForm = false;
    selectedOption = 'file';
    fileContent = '';
    fileName = '';
    fileType = '';
    showUpload = true;
    greetingText =
        "Greetings! I am thrilled to share with you an exciting new tool called BizBrain. Developed by our team, BizBrain is a powerful solution powered by Deloitte.ai. This cutting-edge tool aims to provide valuable insights and analysis for your data. With BizBrain, you can unlock a world of possibilities, allowing you to gain deep understanding and uncover meaningful patterns within your datasets. It's a reliable companion on your data journey, enabling you to make informed decisions and extract valuable knowledge. Get ready to harness the potential of BizBrain and unlock the true power of your data!";
    y;
    greetingBye = `"Thank you all for taking the time to join this demo of bizbrain. We hope you found it informative and insightful. As you have seen, bizbrain has the ability to analyze data and deliver valuable insights that can drive smarter business decisions. We believe that this asset can truly revolutionize the way you understand and utilize your data. If you have any further questions or would like to explore this solution in more detail, please feel free to reach out to our team. Thank you again, and we look forward to potentially working together in the future. Have a great day!"`;
    previousBtnToggleValue: string | null = 'file';
    chartTypes = [
        { value: 'barchart', viewValue: 'Bar chart' },
        { value: 'piechart', viewValue: 'Pie chart' },
        { value: 'linechart', viewValue: 'Line chart' },
    ];

    constructor(
        private formBuilder: FormBuilder,
        private chatService: ChatService,
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private pdfReader: PdfReaderService,
        private dialog: MatDialog,
    ) {}

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any): void {
        console.log('unloadNotification', $event);
    }

    ngOnInit() {
        this.getFormJson();
    }

    generateAudio(text) {
        const key = 'AIzaSyAAYV5DVFzRQHIiqKKNBAS7JXfRh-pEirA';
        const url = `https://texttospeech.googleapis.com/v1/text:synthesize?key=${key}`;
        const params = {
            input: { text },
            voice: {
                name: 'en-US-Standard-C',
                languageCode: 'en-US',
                ssmlGender: 'FEMALE',
            },
            audioConfig: { audioEncoding: 'MP3' },
        };
        this.http.post(url, params).subscribe((response: any) => {
            this.audio = 'data:audio/mpeg;base64,' + response.audioContent;
            this.audioBlobUrl = this.sanitizer.bypassSecurityTrustUrl(this.audio);
        });
    }

    getFormJson() {
        this.chatService.getFormJson().subscribe((res) => {
            this.formJson = res.data['options'];
            this.initForm();
        });
    }

    initForm() {
        this.form = this.formBuilder.group({
            toggleBtn: ['file'],
            additionalInformation: [''],
            dataInput: [''],
            chartType: ['barchart'],
        });

        this.showForm = true;
        this.showUpload = true;
    }

    onSubmit() {
        // as an example for hackathon
        this.getInsights();
        this.drawChart();
    }

    getInsights() {
        const promptInput = PROMPTS.prefix + this.fileContent + this.form.value.dataInput + PROMPTS.getInsights + this.form.value.additionalInformation + PROMPTS.separator;
        this.callPalm(promptInput);
    }

    drawChart() {
        const promptInput = PROMPTS.prefix + this.fileContent + this.form.value.dataInput + this.form.value.chartType + PROMPTS.suffix + this.form.value.additionalInformation + PROMPTS.separator;
        this.callPalm(promptInput);
    }

    callPalm(promptInput: string) {
        const input = {
            promptString: promptInput,
            model: 'text-bison',
        };
        this.displayResponse.emit(input);
    }

    clearFile() {
        this.fileContent = '';
        this.fileName = null;
    }

    emptyInput() {
        this.form.reset();
        this.fileContent = '';
    }

    // Form elements

    // File on drop
    public drop(files: NgxFileDropEntry[]): void {
        this.uploadIcon = true;

        files.forEach((file) => {
            if (file.fileEntry.isFile) {
                const dropped = file.fileEntry as FileSystemFileEntry;
                dropped.file(async (droppedFile: File) => {
                    if (droppedFile) {
                        this.fileName = droppedFile.name;
                        this.fileType = droppedFile.type;
                        if (this.fileType.includes('pdf')) {
                            this.readFromPdf(droppedFile);
                        } else if (this.fileType.includes('xls') || this.fileType.includes('spreadsheetml')) {
                            this.readFromExcel(droppedFile);
                        } else {
                            this.readFileContent(droppedFile);
                        }
                    }
                });
            }
        });
    }

    readFileContent(file: File) {
        const fileReader = new FileReader();
        fileReader.onload = (e) => {
            const fileContent = fileReader.result as string;
            this.fileContent = fileContent;
        };
        fileReader.readAsText(file);
    }

    readFromPdf(file: File) {
        this.pdfReader.readPdf(file).then(
            (text) => {
                this.fileContent = text;
            },
            (reason) => console.error(reason),
        );
    }

    public readFromExcel(file: File): void {
        const reader: FileReader = new FileReader();

        reader.onload = (e: any) => {
            const data: string | ArrayBuffer = e.target.result;
            const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'binary' });

            // Process the workbook data
            const worksheet: XLSX.WorkSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            this.fileContent = JSON.stringify(jsonData);
        };

        reader.readAsBinaryString(file);
    }

    prefillSampleData() {
        this.showUpload = false;
        this.form.patchValue(SAMPLE_DATA.form_value);
    }

    openDialog(field): void {
        const dialogRef = this.dialog.open(BizbrainWarningDialog, {
            data: { name: field.value },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.clearResponse.emit(!result);
                if (field.value === 'file') {
                    this.previousBtnToggleValue = field.value;
                    this.form.get('dataInput').reset();
                    this.showUpload = true;
                } else {
                    this.previousBtnToggleValue = field.value;
                    this.clearFile();
                    this.showUpload = false;
                }
            } else {
                this.form.get('toggleBtn').setValue(this.previousBtnToggleValue);
            }
        });
    }

    resetAll() {
        this.initForm();
    }
}
